import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar } from "@/components/Avatar";
import Logo from "@/components/Logo";
import { HeaderNav } from "@/components/HeaderNav";
import NotificationBell from "@/components/NotificationBell";
import { MessagesLink } from "@/components/support/MessagesLink";
import { StudentModeMenu } from "@/components/StudentModeMenu";
import { MobileNav } from "@/components/MobileNav";
import { Library, LogOut, Shield, User, KeyRound, UserCircle, ChevronDown, MessageSquare, Crown, Users, Repeat, MessagesSquare, GraduationCap } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Header({ variant = "light" }) {
    const { user, profile, logout, switchProfile } = useAuth();
    const nav = useNavigate();
    const isAdmin = user && (user.role === "super_admin" || user.role === "shop_admin");
    const isTeacher = user?.role === "teacher";

    const initials = (user?.name || user?.email || "?")
        .split(/[\s@.]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase())
        .join("") || "•";

    const isDark = variant === "dark";

    return (
        <header
            className={isDark ? "glass-header-dark sticky top-0 z-50" : "glass-header sticky top-0 z-50"}
            data-testid="site-header"
        >
            <div className="max-w-[1600px] mx-auto px-4 sm:px-6 h-20 lg:h-[88px] flex items-center justify-between gap-3 lg:gap-4">
                <div className="flex items-center gap-2 min-w-0">
                    <MobileNav showLibrary={!!user} />
                    <Link to="/" data-testid="logo-link" className="flex-shrink-0">
                        <Logo onDark={isDark} size={40} textClassName="hidden sm:block" />
                    </Link>
                </div>

                <HeaderNav isDark={isDark} showLibrary={!!user} />

                <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                    <span aria-hidden className="hidden xl:block w-px h-10 mr-2" style={{ background: isDark ? "rgba(244,238,223,0.14)" : "rgba(11,15,26,0.10)" }} />
                    {user && !isAdmin && <MessagesLink isDark={isDark} to={isTeacher ? "/prof" : "/messages"} />}
                    {user && !isTeacher && <NotificationBell isDark={isDark} />}
                    {user ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger
                                data-testid="user-menu-trigger"
                                className="flex items-center gap-2 rounded-full pl-1.5 pr-3 py-1 transition-all outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                                style={{
                                    background: isDark ? "rgba(244,238,223,0.08)" : "rgba(11,15,26,0.04)",
                                    border: `1px solid ${isDark ? "rgba(244,238,223,0.14)" : "rgba(11,15,26,0.08)"}`,
                                    color: isDark ? "rgba(244,238,223,0.92)" : "var(--text-primary)",
                                }}
                            >
                                {profile ? (
                                    <Avatar avatar={profile.avatar} name={profile.name} size={32} rounded="full" />
                                ) : (
                                    <span
                                        aria-hidden
                                        className="w-8 h-8 rounded-full flex items-center justify-center font-display text-sm tracking-wide"
                                        style={{
                                            background: "linear-gradient(135deg, var(--gold-400), var(--gold-600))",
                                            color: "#1A1208",
                                        }}
                                    >
                                        {initials}
                                    </span>
                                )}
                                <span className="hidden sm:inline text-sm font-medium max-w-[120px] truncate">
                                    {profile?.name || user.name?.split(" ")[0] || "Mon espace"}
                                </span>
                                <ChevronDown className="w-3.5 h-3.5 opacity-70 hidden sm:inline" />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="end"
                                sideOffset={8}
                                className="w-64 rounded-xl border bg-white shadow-xl p-1.5"
                                style={{ borderColor: "var(--border-light)" }}
                                data-testid="user-menu-content"
                            >
                                <DropdownMenuLabel className="px-3 py-2.5">
                                    <div className="font-display text-base leading-tight" style={{ color: "var(--text-primary)" }}>
                                        {profile?.name || user.name || "Bienvenue"}
                                    </div>
                                    <div className="text-xs mt-0.5 truncate" style={{ color: "var(--text-tertiary)" }}>
                                        {user.email}
                                    </div>
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator style={{ background: "var(--border-light)" }} />
                                {profile && !isAdmin && (
                                    <DropdownMenuItem
                                        onClick={() => { switchProfile(); nav("/profils"); }}
                                        className="rounded-md cursor-pointer focus:bg-[#FAF6EC]"
                                        data-testid="menu-switch-profile"
                                    >
                                        <span className="flex items-center gap-2.5 px-3 py-2.5 text-sm">
                                            <Repeat className="w-4 h-4" style={{ color: "var(--gold-500)" }} />
                                            <span>Changer de profil</span>
                                        </span>
                                    </DropdownMenuItem>
                                )}
                                {profile?.is_admin && !isAdmin && (
                                    <DropdownMenuItem asChild className="rounded-md cursor-pointer focus:bg-[#FAF6EC]">
                                        <Link to="/famille" className="flex items-center gap-2.5 px-3 py-2.5 text-sm" data-testid="menu-family-dashboard">
                                            <Users className="w-4 h-4" style={{ color: "var(--gold-500)" }} />
                                            <span>Tableau de la famille</span>
                                        </Link>
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuItem asChild className="rounded-md cursor-pointer focus:bg-[#FAF6EC]">
                                    <Link to="/library" className="flex items-center gap-2.5 px-3 py-2.5 text-sm" data-testid="menu-library">
                                        <Library className="w-4 h-4" style={{ color: "var(--gold-500)" }} />
                                        <span>Reprendre</span>
                                    </Link>
                                </DropdownMenuItem>
                                {!isAdmin && !isTeacher && (
                                    <DropdownMenuItem asChild className="rounded-md cursor-pointer focus:bg-[#FAF6EC]">
                                        <Link to="/messages" className="flex items-center gap-2.5 px-3 py-2.5 text-sm" data-testid="menu-messages">
                                            <MessagesSquare className="w-4 h-4" style={{ color: "var(--gold-500)" }} />
                                            <span>Mes messages — professeurs</span>
                                        </Link>
                                    </DropdownMenuItem>
                                )}
                                {isTeacher && (
                                    <DropdownMenuItem asChild className="rounded-md cursor-pointer focus:bg-[#FAF6EC]">
                                        <Link to="/prof" className="flex items-center gap-2.5 px-3 py-2.5 text-sm" data-testid="menu-teacher-space">
                                            <GraduationCap className="w-4 h-4" style={{ color: "var(--gold-500)" }} />
                                            <span>Espace professeur</span>
                                        </Link>
                                    </DropdownMenuItem>
                                )}
                                {isAdmin && (
                                    <DropdownMenuItem asChild className="rounded-md cursor-pointer focus:bg-[#FAF6EC]">
                                        <Link to="/admin" className="flex items-center gap-2.5 px-3 py-2.5 text-sm" data-testid="menu-admin">
                                            <Shield className="w-4 h-4" style={{ color: "var(--gold-500)" }} />
                                            <span>Espace admin</span>
                                        </Link>
                                    </DropdownMenuItem>
                                )}
                                {isAdmin && <StudentModeMenu />}
                                {!isAdmin && !isTeacher && (
                                    <DropdownMenuItem asChild className="rounded-md cursor-pointer focus:bg-[#FAF6EC]">
                                        <Link to="/abonnement" className="flex items-center gap-2.5 px-3 py-2.5 text-sm" data-testid="menu-subscription">
                                            <Crown className="w-4 h-4" style={{ color: "var(--gold-500)" }} />
                                            <span>Mon abonnement</span>
                                        </Link>
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator style={{ background: "var(--border-light)" }} />
                                <DropdownMenuItem asChild className="rounded-md cursor-pointer focus:bg-[#FAF6EC]">
                                    <Link to="/account" className="flex items-center gap-2.5 px-3 py-2.5 text-sm" data-testid="menu-account">
                                        <UserCircle className="w-4 h-4 opacity-70" />
                                        <span>Mon profil</span>
                                    </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild className="rounded-md cursor-pointer focus:bg-[#FAF6EC]">
                                    <Link to="/account#password" className="flex items-center gap-2.5 px-3 py-2.5 text-sm" data-testid="menu-password">
                                        <KeyRound className="w-4 h-4 opacity-70" />
                                        <span>Mot de passe</span>
                                    </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild className="rounded-md cursor-pointer focus:bg-[#FAF6EC]">
                                    <Link to="/contact" className="flex items-center gap-2.5 px-3 py-2.5 text-sm" data-testid="menu-support">
                                        <MessageSquare className="w-4 h-4 opacity-70" />
                                        <span>Contacter le support</span>
                                    </Link>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator style={{ background: "var(--border-light)" }} />
                                <DropdownMenuItem
                                    onClick={() => { nav("/", { replace: true }); logout(); }}
                                    className="rounded-md cursor-pointer focus:bg-[#FAF6EC]"
                                    data-testid="menu-logout"
                                >
                                    <span className="flex items-center gap-2.5 px-1 py-1 text-sm" style={{ color: "var(--status-error)" }}>
                                        <LogOut className="w-4 h-4" />
                                        <span>Se déconnecter</span>
                                    </span>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : (
                        <>
                            <Link
                                to="/login"
                                className={`${isDark ? "btn-ghost-dark" : "btn-ghost"} !px-3 2xl:!px-5 !hidden sm:!inline-flex`}
                                data-testid="nav-login"
                            >
                                <User className="w-4 h-4" /> Connexion
                            </Link>
                            <Link to="/register" className="btn-gold text-sm !px-3 sm:!px-4 2xl:!px-6 !py-2 sm:!py-3 whitespace-nowrap" data-testid="nav-register">
                                <span className="2xl:hidden">S&apos;inscrire</span><span className="hidden 2xl:inline">Créer un compte</span>
                            </Link>
                        </>
                    )}
                </div>
            </div>
        </header>
    );
}
